'use client'
import { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'next/navigation'
import { getCookies } from 'cookies-next'
import { countryCodes } from '@constants/countryCodes'
import { preferredContact } from '@constants/preferredContact'

// ── Resolve multilingual value ────────────────────────────────────────────────
function r(val, lang) {
  if (!val) return ''
  if (typeof val === 'string') return val
  return val[lang] ?? val.en ?? Object.values(val)[0] ?? ''
}

// ── Icons ─────────────────────────────────────────────────────────────────────
const Icons = {
  user:   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>,
  email:  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>,
  phone:  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.56 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.29 6.29l1.27-.88a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
  line:   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 11.5C21 6.8 16.97 3 12 3S3 6.8 3 11.5c0 4.2 3.37 7.73 8 8.63v-2.94c-3.07-.74-5-2.7-5-5.69 0-3.03 2.69-5.5 6-5.5s6 2.47 6 5.5c0 .65-.13 1.28-.36 1.87L16 14.5v2h-4v-2h2.08A4.5 4.5 0 0 1 7.5 11.5"/></svg>,
  wechat: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9.5 9a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z" fill="currentColor"/><path d="M14.5 9a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z" fill="currentColor"/><path d="M8.5 15a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z" fill="currentColor"/><path d="M12.5 15a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z" fill="currentColor"/><path d="M2 12c0 4.4 4.03 8 9 8 1.3 0 2.53-.26 3.63-.72L20 21l-1.5-4.05C19.46 15.56 21 13.87 21 12c0-4.42-4.03-8-9-8s-9 3.58-9 8z"/></svg>,
  none:   null,
}

// ── Country combobox ──────────────────────────────────────────────────────────
function CountryCombobox({ value, onChange, inputStyle, radius }) {
  const [query,   setQuery]   = useState('')
  const [open,    setOpen]    = useState(false)
  const [hovered, setHovered] = useState(null)
  const ref = useRef(null)

  const list = query === ''
    ? countryCodes
    : countryCodes.filter(c =>
        c.country.toLowerCase().includes(query.toLowerCase()) ||
        c.code.toLowerCase().includes(query.toLowerCase())
      )

  useEffect(() => {
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setQuery('') } }
    document.addEventListener('mousedown', fn)
    return () => document.removeEventListener('mousedown', fn)
  }, [])

  const pick = (code) => { onChange(code); setQuery(''); setOpen(false) }

  return (
    <div ref={ref} style={{ position: 'relative', width: 100, flexShrink: 0 }}>
      <div style={{ position: 'relative' }}>
        {/* FIX: aria-label gives screen readers a meaningful name for this input */}
        <input
          type="text"
          aria-label="Country code"
          value={open ? query : value}
          onChange={e => { setQuery(e.target.value); setOpen(true) }}
          onFocus={() => { setOpen(true); setQuery('') }}
          onClick={e => { e.target.select(); setOpen(true); setQuery('') }}
          style={{ ...inputStyle, width: '100%', paddingInlineEnd: 28 }}
        />
        {/* FIX: aria-label, aria-expanded, aria-haspopup make this button
            announced correctly instead of just "button" */}
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          aria-label="Select country code"
          aria-expanded={open}
          aria-haspopup="listbox"
          style={{ position: 'absolute', insetInlineEnd: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 0, color: '#9ca3af', cursor: 'pointer', display: 'flex' }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>
        </button>
      </div>
      {open && list.length > 0 && (
        <div
          role="listbox"
          aria-label="Country codes"
          style={{ position: 'absolute', zIndex: 50, top: '100%', insetInlineStart: 0, width: 280, maxHeight: 200, overflowY: 'auto', background: '#fff', border: '1px solid #e5e7eb', borderRadius: radius ?? 6, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', marginTop: 2 }}
        >
          {list.map(c => (
            <div
              key={c.id}
              role="option"
              aria-selected={value === c.code}
              onMouseDown={() => pick(c.code)}
              onMouseEnter={() => setHovered(c.id)}
              onMouseLeave={() => setHovered(null)}
              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, background: hovered === c.id ? '#f3f4f6' : '#fff', color: '#111827' }}
            >
              {c.code} {c.country}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────
export default function RecruitmentFormRenderer({ cardStyle, lang = 'en', userId }) {
  const searchParams = useSearchParams()

  const [isLoading,       setIsLoading]       = useState(false)
  const [hasEnquiry,      setHasEnquiry]      = useState(false)
  const [name,            setName]            = useState('')
  const [email,           setEmail]           = useState('')
  const [phone,           setPhone]           = useState('')
  const [line,            setLine]            = useState('')
  const [wechat,          setWechat]          = useState('')
  const [message,         setMessage]         = useState('')
  const [selectedCountry, setSelectedCountry] = useState(countryCodes[0]?.code ?? '+66')
  const [contact,         setContact]         = useState(preferredContact[lang]?.[0])

  useEffect(() => {
    try {
      const s = JSON.parse(localStorage.getItem('contactDetails'))
      if (!s) return
      if (s.name)            setName(s.name)
      if (s.selectedCountry) setSelectedCountry(s.selectedCountry)
      if (s.phone)           setPhone(s.phone)
      if (s.email)           setEmail(s.email)
      if (s.contact)         setContact(s.contact)
      if (s.line)            setLine(s.line)
      if (s.wechat)          setWechat(s.wechat)
    } catch {}
  }, [])

  const handleForm = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    localStorage.setItem('contactDetails', JSON.stringify({ name, selectedCountry, phone, email, contact, line, wechat }))
    const { sendGAEvent } = await import('@next/third-parties/google')
    sendGAEvent(
    'event',
    'lead_agent_recruitment',  
    { 
      // event: 'lead_agent_recruitment', 
      // user_email: email || 'not_provided', 
      // user_phonecall: `${selectedCountry}${phone?.replace(/[a-zA-Z\W]/g, '')}` 
    })
    const fbclid = searchParams.get('fbclid')
    const { sendAgentRecruitmentForm } = await import('@services/sendAgentRecruitmentForm')
    await sendAgentRecruitmentForm({ userId, name, selectedCountry, phone, email, message, contact, line, wechat })
    setHasEnquiry(true)
    setIsLoading(false)
  }

  if (!cardStyle?.fields || !cardStyle?.formStyle) return null
  const { fields, formStyle: fs } = cardStyle
  const contactVal = contact?.value ?? contact ?? ''

  const inputBase = {
    width: '100%',
    paddingTop: '8px', paddingBottom: '8px',
    paddingInlineStart: 36, paddingInlineEnd: 10,
    boxSizing: 'border-box', fontFamily: 'inherit',
    fontSize: `${fs.inputFontSize ?? 0.875}rem`, color: fs.inputColor ?? '#111827',
    background: fs.inputBg ?? '#ffffff', outline: 'none',
    border: `${fs.inputBorderWidth ?? 1}px solid ${fs.inputBorderColor ?? '#d1d5db'}`,
    borderRadius: fs.inputRadius ?? 6,
  }
  const inputNoIcon = { ...inputBase, paddingInlineStart: 10, paddingInlineEnd: 10 }
  const inputSelect = { ...inputNoIcon, paddingInlineEnd: 32 }

  const lbl = (f) => ({
    display: 'block',
    fontSize:    `${f?.labelSize   ?? fs.labelSize   ?? 0.8125}rem`,
    fontWeight:   f?.labelWeight   ?? fs.labelWeight  ?? '500',
    color:        f?.labelColor    ?? fs.labelColor   ?? '#374151',
    marginBottom: 6,
  })

  const box = {
    background:   fs.bg           ?? '#ffffff',
    border:       (fs.borderWidth ?? 1) > 0 ? `${fs.borderWidth ?? 1}px solid ${fs.borderColor ?? '#e5e7eb'}` : 'none',
    borderRadius: fs.borderRadius ?? 8,
    boxShadow:    fs.shadow       ?? 'none',
    padding:      fs.padding      ?? 24,
  }

  const iconWrapper = {
    position: 'absolute', insetInlineStart: 10,
    top: '50%', transform: 'translateY(-50%)',
    color: '#9ca3af', display: 'flex', pointerEvents: 'none',
  }

  // FIX: aria-hidden on the decorative chevron SVG
  const chevron = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
      style={{ position: 'absolute', insetInlineEnd: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#9ca3af' }}>
      <path d="m6 9 6 6 6-6"/>
    </svg>
  )

  // FIX: aria-hidden so screen readers skip the decorative asterisk
  const reqMark = <span style={{ color: '#ef4444', marginInlineStart: 2 }} aria-hidden="true">*</span>

  const visible = (field) => {
    if (!field.enabled) return false
    if (!field.isConditional) return true
    const { fieldId, values } = field.showWhen
    if (fieldId === 'contact') return values.includes(contactVal)
    return true
  }

  // ── Success state ─────────────────────────────────────────────────────────
  if (hasEnquiry) {
    const align = fs.successTitleAlign ?? 'center'
    return (
      <div style={{ ...box, display: 'flex', flexDirection: 'column', alignItems: align === 'center' ? 'center' : align === 'end' || align === 'right' ? 'flex-end' : 'flex-start', gap: 12, textAlign: align }}>
        <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>
        </div>
        <div style={{ fontSize: `${fs.successTitleSize ?? 1.25}rem`, fontWeight: fs.successTitleWeight ?? '700', color: fs.successTitleColor ?? '#111827' }}>
          {r(fs.successTitle, lang) || 'Application Received!'}
        </div>
        <div style={{ fontSize: `${fs.successSubtitleSize ?? 0.9375}rem`, fontWeight: fs.successSubtitleWeight ?? '400', color: fs.successSubtitleColor ?? '#6b7280', lineHeight: 1.6, maxWidth: 360 }}>
          {r(fs.successSubtitle, lang)}
        </div>
      </div>
    )
  }

  // ── Form ──────────────────────────────────────────────────────────────────
  return (
    <div style={box}>
      {fs.titleEnabled !== false && (
        <div style={{ fontSize: `${fs.titleSize ?? 1.25}rem`, fontWeight: fs.titleWeight ?? '700', color: fs.titleColor ?? '#111827', textAlign: fs.titleAlign ?? 'center', marginBottom: fs.subtitleEnabled ? 6 : (fs.gap ?? 16) }}>
          {r(fs.titleText, lang) || 'Join Our Team'}
        </div>
      )}
      {fs.subtitleEnabled && (
        <div style={{ fontSize: `${fs.subtitleSize ?? 0.9375}rem`, fontWeight: fs.subtitleWeight ?? '400', color: fs.subtitleColor ?? '#6b7280', textAlign: fs.subtitleAlign ?? 'center', marginBottom: fs.gap ?? 16, lineHeight: 1.5 }}>
          {r(fs.subtitleText, lang)}
        </div>
      )}

      <form onSubmit={handleForm} style={{ display: 'flex', flexDirection: 'column', gap: fs.gap ?? 16 }}>
        {fields.filter(visible).map(field => {
          const icon    = Icons[field.icon ?? 'none']
          const hasIcon = !!icon
          const label   = r(field.label, lang)
          const ph      = r(field.placeholder, lang)
          const req     = field.required !== false
          const iconEl  = hasIcon
            ? <span style={iconWrapper}>{icon}</span>
            : null

          // ── Contact method select ──────────────────────────────────────
          // FIX: htmlFor + id association for the contact select
          if (field.id === 'contact') return (
            <div key={field.id}>
              <label htmlFor="field-contact" style={lbl(field)}>{label}{req && reqMark}</label>
              <div style={{ position: 'relative' }}>
                <select
                  id="field-contact"
                  value={contactVal} required={req}
                  onChange={e => setContact(preferredContact[lang]?.find(x => x.value === e.target.value) ?? e.target.value)}
                  style={{ ...inputSelect, appearance: 'none', cursor: 'pointer' }}
                >
                  {preferredContact[lang]?.map((c, i) => <option key={i} value={c.value}>{c.name}</option>)}
                </select>
                {chevron}
              </div>
            </div>
          )

          // ── Phone + country code ───────────────────────────────────────
          // FIX: phone input gets aria-label since it shares a visual label
          // with the country combobox (a <label> can't link to two inputs)
          if (field.id === 'phone') return (
            <div key={field.id}>
              <label style={lbl(field)}>{label}{req && reqMark}</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <CountryCombobox value={selectedCountry} onChange={setSelectedCountry} inputStyle={inputNoIcon} radius={fs.inputRadius ?? 6} />
                <div style={{ position: 'relative', flex: 1 }}>
                  {iconEl}
                  <input
                    type="text"
                    aria-label={label}
                    value={phone} onChange={e => setPhone(e.target.value)}
                    placeholder={ph} required={req}
                    style={hasIcon ? inputBase : inputNoIcon}
                  />
                </div>
              </div>
            </div>
          )

          // ── Textarea ───────────────────────────────────────────────────
          // FIX: htmlFor + id association for textarea
          if (field.type === 'textarea') return (
            <div key={field.id}>
              <label htmlFor={`field-${field.id}`} style={lbl(field)}>{label}{req && reqMark}</label>
              <textarea
                id={`field-${field.id}`}
                value={message} onChange={e => setMessage(e.target.value)}
                placeholder={ph} required={req} rows={field.rows ?? 4}
                style={{ ...inputNoIcon, resize: 'vertical' }}
              />
            </div>
          )

          // ── Email ──────────────────────────────────────────────────────
          // FIX: htmlFor + id association for email input
          if (field.type === 'email') return (
            <div key={field.id}>
              <label htmlFor={`field-${field.id}`} style={lbl(field)}>{label}{req && reqMark}</label>
              <div style={{ position: 'relative' }}>
                {iconEl}
                <input
                  id={`field-${field.id}`}
                  type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder={ph} required={req}
                  style={hasIcon ? inputBase : inputNoIcon}
                />
              </div>
            </div>
          )

          // ── Generic text: name / line / wechat ─────────────────────────
          // FIX: htmlFor + id association for all generic text inputs
          const textVal    = field.id === 'name' ? name : field.id === 'line' ? line : wechat
          const textSetter = field.id === 'name' ? setName : field.id === 'line' ? setLine : setWechat
          return (
            <div key={field.id}>
              <label htmlFor={`field-${field.id}`} style={lbl(field)}>{label}{req && reqMark}</label>
              <div style={{ position: 'relative' }}>
                {iconEl}
                <input
                  id={`field-${field.id}`}
                  type="text" value={textVal} onChange={e => textSetter(e.target.value)}
                  placeholder={ph} required={req}
                  style={hasIcon ? inputBase : inputNoIcon}
                />
              </div>
            </div>
          )
        })}

        <button type="submit" disabled={isLoading}
          style={{ width: fs.btnFullWidth !== false ? '100%' : 'auto', padding: '10px 20px', marginTop: 4, fontFamily: 'inherit', cursor: isLoading ? 'not-allowed' : 'pointer', fontSize: `${fs.btnFontSize ?? 0.9375}rem`, fontWeight: fs.btnFontWeight ?? '600', color: fs.btnColor ?? '#ffffff', background: isLoading ? `${fs.btnBg ?? '#2563eb'}99` : (fs.btnBg ?? '#2563eb'), border: 'none', borderRadius: fs.btnRadius ?? 6, transition: 'opacity 0.2s' }}>
          {isLoading ? '…' : (r(fs.btnText, lang) || 'Submit Application')}
        </button>
      </form>
    </div>
  )
}